import { useState, type FormEvent, type ReactNode } from "react";
import { Eye, EyeOff, type LucideIcon } from "lucide-react";

interface AuthFieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon: LucideIcon;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  disabled?: boolean;
}

export function AuthField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  icon: Icon,
  autoComplete,
  required,
  minLength,
  disabled,
}: AuthFieldProps) {
  const isPassword = type === "password";
  const [revealed, setRevealed] = useState(false);
  const inputType = isPassword ? (revealed ? "text" : "password") : type;

  return (
    <label className="auth-field" htmlFor={id}>
      <span className="auth-field__label">{label}</span>
      <span className="auth-field__control">
        <Icon className="auth-field__icon" strokeWidth={2.25} aria-hidden />
        <input
          id={id}
          type={inputType}
          className={`auth-field__input${isPassword ? " auth-field__input--password" : ""}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          minLength={minLength}
          disabled={disabled}
        />
        {isPassword ? (
          <button
            type="button"
            className="auth-field__reveal"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? "Hide password" : "Show password"}
            aria-pressed={revealed}
            tabIndex={0}
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            ) : (
              <Eye className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            )}
          </button>
        ) : null}
      </span>
    </label>
  );
}

export function AuthSubmit({
  loading,
  children,
}: {
  loading?: boolean;
  children: ReactNode;
}) {
  return (
    <button type="submit" className="btn-primary auth-submit w-full" disabled={loading}>
      {children}
    </button>
  );
}

export type { FormEvent };
